import { useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DailyExpenseEvent,
  DailyExpenseEventAction,
} from "../../types/daily-expense.type";
import { numberWithCommas } from "../../utils";
import { EVENT_LABEL } from "./dailyExpenseStatus";
import { formatTimestamp } from "./format";

const DOT_SIZE = 10;

const DOT_COLOR: Record<DailyExpenseEventAction, string> = {
  SUBMIT: "primary.main",
  SUBMIT_NO_EXPENSE: "primary.main",
  APPROVE: "success.main",
  REJECT: "error.main",
};

function HistoryTimeline({ history }: Readonly<{ history: DailyExpenseEvent[] }>) {
  if (history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        ยังไม่เคยส่ง
      </Typography>
    );
  }
  return (
    <Stack>
      {history.map((event, index) => (
        <Stack key={`${event.action}-${event.acted_at}`} direction="row" gap={1.5}>
          <Stack alignItems="center">
            <Box
              sx={{
                mt: 0.75,
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: "50%",
                bgcolor: DOT_COLOR[event.action],
              }}
            />
            {index < history.length - 1 && <Box sx={{ flex: 1, width: 2, bgcolor: "divider" }} />}
          </Stack>
          <Stack sx={{ pb: 1.5, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {EVENT_LABEL[event.action]}{" "}
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                · {event.actor_name ?? event.actor} · {formatTimestamp(event.acted_at)}
              </Box>
            </Typography>
            {event.reason && (
              <Typography variant="body2" color="text.secondary">
                {event.reason}
              </Typography>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

interface ExpenseHistoryProps {
  /** Newest first, as the server sends it. */
  history: DailyExpenseEvent[];
  /** The S2 form folds it away; the head-office drawer always shows it. */
  collapsible?: boolean;
}

/** ประวัติ: every ส่ง / อนุมัติ / ตีกลับ of the day, with the reason of each send-back. */
export default function ExpenseHistory({ history, collapsible = false }: Readonly<ExpenseHistoryProps>) {
  const [isOpen, setOpen] = useState(false);
  const toggleOpen = () => setOpen((previous) => !previous);
  const heading = (
    <Typography fontWeight={600}>
      ประวัติ{" "}
      <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
        · {numberWithCommas(history.length)} รายการ
      </Box>
    </Typography>
  );

  if (!collapsible) {
    return (
      <Stack gap={1}>
        {heading}
        <HistoryTimeline history={history} />
      </Stack>
    );
  }
  return (
    <Stack>
      <ButtonBase
        onClick={toggleOpen}
        aria-expanded={isOpen}
        sx={{ justifyContent: "space-between", py: 0.5, borderRadius: 1 }}
      >
        {heading}
        <ExpandMoreIcon
          sx={{ color: "text.secondary", transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </ButtonBase>
      <Collapse in={isOpen}>
        <Box sx={{ pt: 1.25 }}>
          <HistoryTimeline history={history} />
        </Box>
      </Collapse>
    </Stack>
  );
}
